import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import pinia from "@/store";
import vuetify from '@/vuetify'
import { IonicVue } from '@ionic/vue';
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";
import "./styles/styles.scss";
import { crashlytics } from '@/services/crashlytics';
import { liveUpdates } from '@/services/liveUpdates';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

const app = createApp(App);

if (import.meta.env.VITE_ENV !== 'local') {
   const sentryPinia = createPinia();
   sentryPinia.use(createSentryPiniaPlugin());
   Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_ENV,
      release: import.meta.env.VITE_RELEASE,
   });
}

app.use(pinia)
    .use(IonicVue)
    .use(vuetify)
    .use(router);

// Start the mandatory update gate before router readiness. The gate calls
// notifyAppReady() as its first native updater operation, within Capgo's 10-second
// watchdog, then blocks native mount until Capgo conclusively reports that no
// update is needed. Required updates reload the webview; failures stay behind a
// blocking Retry screen. Router/auth work proceeds in parallel so a successful
// check adds only the time not already spent preparing navigation.
const updateReady = liveUpdates.requireLatestVersion();
const routerReady = router.isReady();

Promise.all([updateReady, routerReady]).then(() => {
   try {
      if (import.meta.env.VITE_ENV === 'local') {
         window.vueinstance = app.mount('#app');
      } else {
         app.mount('#app');
      }
      crashlytics.initialize();
   } catch (error) {
      Sentry.captureException(error);
      crashlytics.recordException(`app mount failed: ${error?.message ?? error}`);
      throw error;
   }

   // The raw-DOM "Checking for updates…" overlay (liveUpdates.js) is deliberately
   // left in place by requireLatestVersion() rather than removed as soon as the
   // update check resolves — it sits on top of App.vue's own "Connecting…"
   // overlay (z-index 999999 vs 99999) until we remove it here. Wait two paint
   // frames: the first lets the browser flush Vue's just-mounted DOM, the second
   // confirms that frame actually rendered, so the Vue overlay is guaranteed to
   // be visible underneath before the raw-DOM one is torn down. Without this,
   // there is a multi-tick gap between hiding the raw-DOM overlay and Vue's
   // first paint where neither overlay covers the screen.
   window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
         liveUpdates.hideStartupOverlay();
      });
   });
}).catch((error) => {
   Sentry.captureException(error);
   crashlytics.recordException(`app bootstrap failed: ${error?.message ?? error}`);
});
